import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  CalendarRange,
  ChevronLeft,
  ClipboardCheck,
  ClipboardList,
  MapPin,
  Paperclip,
  Plus,
  Save,
} from 'lucide-react';
import toast from 'react-hot-toast';
import customerService from '../../../../services/customerService';
import locationService from '../../../../services/locationService';
import projectTypeService, { type ProjectType } from '../../../../services/projectTypeService';
import { SOVEREIGN } from '../../utils/epcProjectVisual';
import WeightedProgress, { type TabDef } from './components/WeightedProgress';
import { dateInputToIso, durationParts, isoToDateInput } from './format';
import AddCustomerModal from './panels/AddCustomerModal';
import AddLocationModal, { type CreatedLocation } from './panels/AddLocationModal';
import { TAB_ORDER, overallProgress, type TabId } from './progress';
import BasicsStep from './steps/BasicsStep';
import DocsStep from './steps/DocsStep';
import LocationStep from './steps/LocationStep';
import OverviewStep, { type CreatedProject } from './steps/OverviewStep';
import TimelineStep from './steps/TimelineStep';
import type {
  EpcProjectWizardProps,
  ExistingDocRow,
  LocationOption,
  PendingFile,
  WizardOption,
  WizardSubmitPayload,
} from './types';
import {
  DOC_TAG_BY_VALUE,
  PRIMARY_BTN,
  THEME_STYLES,
  TILE,
  deriveDisplayName,
  detectTagFromName,
  fileExt,
  svSecondaryBtn,
  themeFromCategory,
} from './ui';

// Public API re-exports so pages import everything from this module.
export type {
  DocTag,
  EpcProjectWizardProps,
  ExistingDoc,
  WizardInitialValues,
  WizardMode,
  WizardOption,
  WizardSubmitPayload,
  WizardSubmitResult,
} from './types';

const STATUS_OPTIONS: WizardOption[] = [
  { value: 'active', label: 'Under construction' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'completed', label: 'Commissioned' },
  { value: 'on_hold', label: 'On hold' },
];

const TABS: TabDef[] = [
  { id: 'basics', number: 1, label: 'Project Basics', hint: 'Code, name, type & customer', icon: ClipboardCheck },
  { id: 'location', number: 2, label: 'Site & Location', hint: 'Where the project lives', icon: MapPin },
  { id: 'timeline', number: 3, label: 'Capacity & Timeline', hint: 'MW and key dates', icon: CalendarRange },
  { id: 'docs', number: 4, label: 'Documents & Notes', hint: 'Files and remarks', icon: Paperclip },
  { id: 'review', number: 5, label: 'Review & Create', hint: 'Check everything, then ship', icon: ClipboardList },
];

function generateProjectCode(): string {
  const t = Date.now().toString(36).toUpperCase().slice(-6);
  const r = Math.random().toString(36).slice(2, 5).toUpperCase();
  return `PRJ-${t}${r}`;
}

function suggestCodeFromName(name: string): string {
  const slug = name
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 14)
    .toUpperCase();
  if (!slug) return generateProjectCode();
  return `${slug}-${Math.random().toString(36).slice(2, 4).toUpperCase()}`;
}

function parseMwInput(raw: string): number | undefined {
  const s = raw.trim();
  if (s === '') return undefined;
  const n = Number(s);
  if (Number.isNaN(n) || n < 0) return undefined;
  return Math.round(n * 100) / 100;
}

function toNum(v: unknown): number | undefined {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

export default function EpcProjectWizard({
  mode,
  initialValues,
  existingDocs,
  subtitle,
  submitting,
  onSubmit,
  onCancel,
}: EpcProjectWizardProps) {
  const navigate = useNavigate();

  const [code, setCode] = useState(initialValues?.code ?? '');
  const [codeTouched, setCodeTouched] = useState(Boolean(initialValues?.code));
  const [name, setName] = useState(initialValues?.name ?? '');
  const [customerId, setCustomerId] = useState(initialValues?.customerId ?? '');
  const [projectTypeId, setProjectTypeId] = useState(initialValues?.projectTypeId ?? '');
  const [locationSelection, setLocationSelection] = useState<LocationOption[]>([]);
  const [dcMw, setDcMw] = useState(initialValues?.dcCapacityMw != null ? String(initialValues.dcCapacityMw) : '');
  const [acMw, setAcMw] = useState(initialValues?.acCapacityMw != null ? String(initialValues.acCapacityMw) : '');
  const [status] = useState(initialValues?.status ?? 'active');
  const [commissioningDate, setCommissioningDate] = useState(isoToDateInput(initialValues?.commissioningDate));
  const [startDate, setStartDate] = useState(isoToDateInput(initialValues?.startDate));
  const [endDatePlanned, setEndDatePlanned] = useState(isoToDateInput(initialValues?.endDatePlanned));
  // PM is not editable in this wizard (managed on the team page); we still preserve its value through edits.
  const [projectManager] = useState(initialValues?.projectManager ?? '');
  const [remarks, setRemarks] = useState(initialValues?.remarks ?? '');

  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [customerOptions, setCustomerOptions] = useState<WizardOption[]>([]);
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const [loadingMasters, setLoadingMasters] = useState(true);

  const [showCustomerPanel, setShowCustomerPanel] = useState(false);
  const [showLocationPanel, setShowLocationPanel] = useState(false);

  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [existingRows, setExistingRows] = useState<ExistingDocRow[]>(() =>
    (existingDocs ?? []).map((d, i) => ({
      ...d,
      id: `existing-${i}-${d.fileKey}`,
      tag: detectTagFromName(d.fileName),
      displayName: deriveDisplayName(d.fileName),
    }))
  );
  const [tabId, setTabId] = useState<TabId>('basics');
  const [dragOver, setDragOver] = useState(false);

  const [codeError, setCodeError] = useState<string | null>(null);
  const [errorTab, setErrorTab] = useState<TabId | null>(null);
  const [created, setCreated] = useState<CreatedProject | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Load masters
  useEffect(() => {
    const load = async () => {
      setLoadingMasters(true);
      try {
        const [types, custRes, locRes] = await Promise.all([
          projectTypeService.getProjectTypesForComponent(),
          customerService.getCustomers({ limit: 300, status: 'active', page: 1 }),
          locationService.getLocations({ limit: 300, status: 'active', page: 1 }),
        ]);
        setProjectTypes(types);
        const custRows = 'meta' in custRes ? custRes.data : custRes.data;
        setCustomerOptions(
          custRows.map((c) => ({
            value: c._id,
            label: c.code ? `${c.name} (${c.code})` : c.name,
          }))
        );
        const locOpts: LocationOption[] = (locRes.data ?? []).map((loc) => ({
          value: String(loc._id),
          label: loc.code ? `${loc.name} · ${loc.code}` : loc.name,
          name: loc.name,
          code: loc.code,
          state: loc.state ?? loc.mainAddress?.state,
          city: loc.city ?? loc.mainAddress?.city,
          lat: toNum(loc.latitude ?? loc.mainAddress?.coordinates?.latitude),
          lng: toNum(loc.longitude ?? loc.mainAddress?.coordinates?.longitude),
        }));
        setLocationOptions(locOpts);

        // Re-hydrate location selection from initialValues once options are loaded.
        if (initialValues?.locationIds && initialValues.locationIds.length) {
          const map = new Map(locOpts.map((o) => [o.value, o]));
          const seeded = initialValues.locationIds
            .map((id) => map.get(String(id)))
            .filter((o): o is LocationOption => Boolean(o));
          setLocationSelection(seeded);
        }
      } catch {
        toast.error('Could not load customers, locations, or project types');
      } finally {
        setLoadingMasters(false);
      }
    };
    void load();
    // We deliberately only run once on mount — initialValues is captured at first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedType = useMemo(
    () => projectTypes.find((p) => (p._id || p.id) === projectTypeId),
    [projectTypes, projectTypeId]
  );

  const theme = themeFromCategory(selectedType?.category);
  const themeStyles = THEME_STYLES[theme];

  const showDcMw = selectedType?.category === 'solar' || selectedType?.category === 'hybrid';
  const showAcMw =
    selectedType?.category === 'solar' ||
    selectedType?.category === 'wind' ||
    selectedType?.category === 'hybrid' ||
    selectedType?.category === 'bess';

  // Auto-suggest code from name only in create mode (until user types in the code field).
  useEffect(() => {
    if (mode !== 'create') return;
    if (codeTouched) return;
    if (!name.trim()) return;
    setCode(suggestCodeFromName(name));
  }, [mode, name, codeTouched]);

  const onAutoCode = useCallback(() => {
    const next = name.trim() ? suggestCodeFromName(name) : generateProjectCode();
    setCode(next);
    setCodeTouched(true);
    setCodeError(null);
    setErrorTab(null);
    toast.success('Project code regenerated');
  }, [name]);

  const onCustomerCreated = useCallback((customer: { _id: string; name: string; code?: string }) => {
    const opt: WizardOption = {
      value: customer._id,
      label: customer.code ? `${customer.name} (${customer.code})` : customer.name,
    };
    setCustomerOptions((prev) => [...prev, opt]);
    setCustomerId(customer._id);
    setShowCustomerPanel(false);
  }, []);

  const onLocationCreated = useCallback((loc: CreatedLocation) => {
    const opt: LocationOption = {
      value: loc._id,
      label: loc.code ? `${loc.name} · ${loc.code}` : loc.name,
      name: loc.name,
      code: loc.code,
      state: loc.state,
      city: loc.city,
      lat: loc.lat,
      lng: loc.lng,
    };
    setLocationOptions((prev) => [...prev, opt]);
    setLocationSelection((prev) => [...prev, opt]);
    setShowLocationPanel(false);
  }, []);

  /* ----------------------- Files ----------------------- */

  const onPickFiles = (list: FileList | null) => {
    if (!list?.length) return;
    const next: PendingFile[] = [];
    for (let i = 0; i < list.length; i++) {
      const file = list[i];
      next.push({
        id: `${file.name}-${Date.now()}-${i}`,
        file,
        tag: 'other',
        displayName: deriveDisplayName(file.name),
      });
    }
    setPendingFiles((prev) => [...prev, ...next]);
  };

  const removePending = (id: string) => setPendingFiles((prev) => prev.filter((p) => p.id !== id));
  const updatePending = (id: string, patch: Partial<Pick<PendingFile, 'tag' | 'displayName'>>) =>
    setPendingFiles((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const removeExisting = (id: string) => setExistingRows((prev) => prev.filter((p) => p.id !== id));
  const updateExisting = (id: string, patch: Partial<Pick<ExistingDocRow, 'tag' | 'displayName'>>) =>
    setExistingRows((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const onDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragOver(false);
    onPickFiles(e.dataTransfer.files);
  };

  /* ----------------------- Validation & progress ----------------------- */

  const dcNum = parseMwInput(dcMw);
  const acNum = parseMwInput(acMw);
  const acExceedsDc = showDcMw && showAcMw && dcNum != null && acNum != null && acNum > dcNum;
  const startEndInverted = Boolean(startDate && endDatePlanned && new Date(startDate) > new Date(endDatePlanned));
  const durationDays = durationParts(startDate, endDatePlanned)?.days ?? null;

  const totalDocs = pendingFiles.length + existingRows.length;

  const requiredOk = Boolean(
    code.trim() && name.trim() && projectTypeId && locationSelection.length > 0 &&
    startDate && endDatePlanned && !startEndInverted && !acExceedsDc
  );

  const missing = useMemo(() => {
    const m: string[] = [];
    if (!code.trim()) m.push('Project code (Basics)');
    if (!name.trim()) m.push('Project name (Basics)');
    if (!projectTypeId) m.push('Project type (Basics)');
    if (!locationSelection.length) m.push('At least one site location');
    if (!startDate) m.push('Start date (Capacity & Timeline)');
    if (!endDatePlanned) m.push('Planned end date (Capacity & Timeline)');
    if (startEndInverted) m.push('End date must be on or after start date');
    if (acExceedsDc) m.push('AC capacity cannot exceed DC capacity');
    return m;
  }, [code, name, projectTypeId, locationSelection.length, startDate, endDatePlanned, startEndInverted, acExceedsDc]);

  const completion: Record<TabId, number> = {
    basics: [code.trim(), name.trim(), projectTypeId, customerId].filter(Boolean).length / 4,
    location: locationSelection.length > 0 ? 1 : 0,
    timeline: (() => {
      const flags: boolean[] = [];
      if (showDcMw) flags.push(dcNum != null);
      if (showAcMw) flags.push(acNum != null);
      flags.push(Boolean(startDate), Boolean(endDatePlanned && !startEndInverted), Boolean(commissioningDate));
      return flags.filter(Boolean).length / flags.length;
    })(),
    docs: (totalDocs > 0 ? 0.6 : 0) + (remarks.trim() ? 0.4 : 0),
    review: requiredOk ? 1 : 0,
  };
  const progressPct = overallProgress(completion);

  /* ----------------------- Navigation & submit ----------------------- */

  const tabIndex = TAB_ORDER.indexOf(tabId);
  const goToTab = (id: TabId) => {
    if (!created) setTabId(id);
  };
  const nextTab = () => {
    const n = TAB_ORDER[tabIndex + 1];
    if (n) setTabId(n);
  };
  const prevTab = () => {
    const p = TAB_ORDER[tabIndex - 1];
    if (p) setTabId(p);
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    else navigate('/epc/projects');
  };

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    // Enter key in any input fires onSubmit — if not on the last tab, just advance.
    if (tabIndex < TAB_ORDER.length - 1) {
      nextTab();
      return;
    }
    if (created) return;
    if (!requiredOk) {
      toast.error('Complete the highlighted items before creating the project');
      return;
    }

    const payload: WizardSubmitPayload = {
      code: code.trim(),
      name: name.trim(),
      customerId: customerId || undefined,
      projectTypeId,
      locationIds: locationSelection.map((o) => o.value).filter(Boolean),
      dcCapacityMw: dcNum,
      acCapacityMw: acNum,
      status,
      commissioningDate: dateInputToIso(commissioningDate),
      startDate: dateInputToIso(startDate),
      endDatePlanned: dateInputToIso(endDatePlanned),
      projectManager: projectManager.trim() || undefined,
      remarks: remarks.trim() || undefined,
      newAttachments: pendingFiles.map((pf) => ({
        file: pf.file,
        tag: pf.tag,
        displayName: pf.displayName.trim() || deriveDisplayName(pf.file.name),
        tagLabel: DOC_TAG_BY_VALUE[pf.tag].short,
      })),
      keptAttachments: existingRows.map((row) => {
        const ext = fileExt(row.fileName);
        const renamedBase = (row.displayName.trim() || deriveDisplayName(row.fileName)) + ext;
        const finalName = `[${DOC_TAG_BY_VALUE[row.tag].short}] ${renamedBase}`;
        return { fileName: finalName, fileKey: row.fileKey, uploadedAt: row.uploadedAt };
      }),
    };

    try {
      const res = await onSubmit(payload);
      if (mode === 'create' && res && typeof res === 'object' && '_id' in res) {
        setCreated({ id: String(res._id), code: payload.code, name: payload.name });
      }
    } catch (err: unknown) {
      let msg = mode === 'edit' ? 'Could not save project' : 'Could not create project';
      let httpStatus: number | undefined;
      if (axios.isAxiosError(err)) {
        httpStatus = err.response?.status;
        const d = err.response?.data as { message?: string; detail?: string } | undefined;
        msg = d?.detail || d?.message || err.message || msg;
      } else if (err instanceof Error) {
        msg = err.message;
      }
      const codeClash = httpStatus === 409 || /code.*(exist|duplicate|taken)|duplicate.*code/i.test(msg);
      if (codeClash) {
        setCodeError(msg);
        setErrorTab('basics');
        setTabId('basics');
      } else {
        toast.error(msg);
      }
    }
  };

  const customerLabel = customerOptions.find((o) => o.value === customerId)?.label.replace(/\s*\([^)]*\)\s*$/, '') ?? '';
  const statusLabel = STATUS_OPTIONS.find((o) => o.value === status)?.label ?? '';

  const isEdit = mode === 'edit';
  const submitLabel = isEdit ? 'Save changes' : 'Create project';
  const SubmitIcon = isEdit ? Save : Plus;
  const activeTab = TABS[tabIndex];
  const ActiveTabIcon = activeTab.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-full"
      style={{ fontFeatureSettings: '"tnum","ss01"', background: SOVEREIGN.paper }}
    >
      {/* Sticky header */}
      <div
        className="sticky top-0 z-20"
        style={{
          background: 'rgba(250,247,238,0.97)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid rgba(11,59,46,0.18)',
          boxShadow: '0 4px 12px -8px rgba(42,31,23,0.15)',
        }}
      >
        <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-12 items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className="flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition hover:opacity-90"
                style={svSecondaryBtn}
              >
                <ArrowLeft size={14} />
                <span className="hidden sm:inline">Back</span>
              </button>
              <div className="hidden h-6 w-px bg-[rgba(11,59,46,0.18)] sm:block" />
              <div className="flex min-w-0 items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#7C8A82]">
                <span>Home</span>
                <span aria-hidden className="text-[rgba(11,59,46,0.25)]">/</span>
                <span>EPC Projects</span>
                <span aria-hidden className="text-[rgba(11,59,46,0.25)]">/</span>
                <span className="text-[#1F5A47]">{isEdit ? 'Edit' : 'New'}</span>
                {isEdit && subtitle ? (
                  <>
                    <span aria-hidden className="text-[rgba(11,59,46,0.25)]">/</span>
                    <span className="min-w-0 truncate normal-case tracking-normal text-[#2A1F17]">{subtitle}</span>
                  </>
                ) : null}
              </div>
            </div>
            <div
              className="hidden shrink-0 items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold sm:inline-flex"
              style={svSecondaryBtn}
              title="Weighted form completeness"
            >
              <span className="font-mono font-bold tabular-nums" style={{ color: SOVEREIGN.title }}>{progressPct}%</span>
              <span style={{ color: SOVEREIGN.kpiCaption }}>complete</span>
            </div>
          </div>

          <WeightedProgress tabs={TABS} completion={completion} activeId={tabId} errorTab={errorTab} onJump={goToTab} />
        </div>
      </div>

      {/* Body */}
      <div className="mx-auto max-w-screen-xl px-4 py-6 sm:px-6 lg:px-8 sm:py-8">
        <form onSubmit={(e) => void handleSubmit(e)} className="min-w-0">
          <div
            className="relative overflow-hidden rounded-2xl"
            style={{
              background: 'rgba(250,247,238,0.96)',
              border: '1px solid rgba(11,59,46,0.35)',
              boxShadow: '0 18px 44px -20px rgba(42,31,23,0.40)',
            }}
          >
            {/* Tab header */}
            <div
              className="px-5 py-4 sm:px-7"
              style={{ borderBottom: '1px solid rgba(11,59,46,0.14)', background: 'linear-gradient(180deg,#F8F2E2 0%,rgba(242,234,219,0.5) 100%)' }}
            >
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-sm" style={{ background: TILE }}>
                  <ActiveTabIcon className="h-5 w-5" strokeWidth={2} />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: SOVEREIGN.brand }}>
                    Tab {tabIndex + 1} of {TAB_ORDER.length}
                  </p>
                  <h2 className="text-base font-extrabold tracking-tight sm:text-lg" style={{ color: SOVEREIGN.title }}>
                    {isEdit && activeTab.id === 'review' ? 'Review & Save' : activeTab.label}
                  </h2>
                  <p className="text-xs" style={{ color: SOVEREIGN.kpiCaption }}>{activeTab.hint}</p>
                </div>
              </div>
            </div>

            <div className="relative min-h-[400px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={tabId}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                  className="p-5 sm:p-7"
                >
                  {tabId === 'basics' && (
                    <BasicsStep
                      code={code}
                      setCode={(v) => {
                        setCode(v.toUpperCase());
                        setCodeTouched(true);
                        setCodeError(null);
                        setErrorTab(null);
                      }}
                      onAutoCode={onAutoCode}
                      codeError={codeError}
                      name={name}
                      setName={setName}
                      projectTypes={projectTypes}
                      projectTypeId={projectTypeId}
                      setProjectTypeId={setProjectTypeId}
                      customerOptions={customerOptions}
                      customerId={customerId}
                      setCustomerId={setCustomerId}
                      loadingMasters={loadingMasters}
                      onAddCustomer={() => setShowCustomerPanel(true)}
                    />
                  )}
                  {tabId === 'location' && (
                    <LocationStep
                      locationOptions={locationOptions}
                      locationSelection={locationSelection}
                      setLocationSelection={setLocationSelection}
                      loadingMasters={loadingMasters}
                      onAddLocation={() => setShowLocationPanel(true)}
                    />
                  )}
                  {tabId === 'timeline' && (
                    <TimelineStep
                      showDcMw={showDcMw}
                      showAcMw={showAcMw}
                      dcMw={dcMw}
                      setDcMw={setDcMw}
                      acMw={acMw}
                      setAcMw={setAcMw}
                      acExceedsDc={acExceedsDc}
                      startDate={startDate}
                      setStartDate={setStartDate}
                      endDatePlanned={endDatePlanned}
                      setEndDatePlanned={setEndDatePlanned}
                      commissioningDate={commissioningDate}
                      setCommissioningDate={setCommissioningDate}
                      durationDays={durationDays}
                      startEndInverted={startEndInverted}
                      accent={themeStyles.accent}
                    />
                  )}
                  {tabId === 'docs' && (
                    <DocsStep
                      remarks={remarks}
                      setRemarks={setRemarks}
                      pendingFiles={pendingFiles}
                      existingRows={existingRows}
                      removePending={removePending}
                      updatePending={updatePending}
                      removeExisting={removeExisting}
                      updateExisting={updateExisting}
                      onPickFiles={(e: ChangeEvent<HTMLInputElement>) => {
                        onPickFiles(e.target.files);
                        e.target.value = '';
                      }}
                      onDrop={onDrop}
                      dragOver={dragOver}
                      setDragOver={setDragOver}
                      fileInputRef={fileInputRef}
                    />
                  )}
                  {tabId === 'review' && (
                    <OverviewStep
                      mode={mode}
                      created={created}
                      onJump={goToTab}
                      missing={missing}
                      code={code}
                      name={name}
                      typeLabel={selectedType?.name ?? ''}
                      customerLabel={customerLabel}
                      statusLabel={statusLabel}
                      locations={locationSelection}
                      showDcMw={showDcMw}
                      showAcMw={showAcMw}
                      dcMw={dcNum}
                      acMw={acNum}
                      startDate={startDate}
                      endDatePlanned={endDatePlanned}
                      commissioningDate={commissioningDate}
                      accent={themeStyles.accent}
                      docNames={[...existingRows.map((r) => r.displayName), ...pendingFiles.map((p) => p.displayName)]}
                      remarks={remarks}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Footer nav — hidden after success */}
            {!created ? (
              <div
                className="relative flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6"
                style={{ borderTop: '1px solid rgba(11,59,46,0.14)', background: 'rgba(242,234,219,0.6)' }}
              >
                <button
                  type="button"
                  onClick={prevTab}
                  disabled={tabIndex <= 0}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition disabled:pointer-events-none disabled:opacity-40"
                  style={svSecondaryBtn}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                <p className="order-first text-xs sm:order-none" style={{ color: SOVEREIGN.kpiCaption }}>
                  Tab <span className="font-bold" style={{ color: SOVEREIGN.title }}>{tabIndex + 1}</span> of {TAB_ORDER.length}
                  <span> · </span>
                  {activeTab.hint}
                </p>
                {tabIndex < TAB_ORDER.length - 1 ? (
                  <button
                    type="button"
                    onClick={nextTab}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-white transition hover:opacity-95"
                    style={PRIMARY_BTN}
                  >
                    Next
                    <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={submitting || loadingMasters || !requiredOk}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg px-5 py-2 text-sm font-semibold text-white transition hover:opacity-95 disabled:opacity-50"
                    style={PRIMARY_BTN}
                  >
                    {submitting ? (
                      isEdit ? 'Saving…' : 'Creating…'
                    ) : (
                      <>
                        <SubmitIcon className="h-4 w-4" strokeWidth={2.4} />
                        {submitLabel}
                      </>
                    )}
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </form>
      </div>

      <AddCustomerModal open={showCustomerPanel} onClose={() => setShowCustomerPanel(false)} onCreated={onCustomerCreated} />
      <AddLocationModal open={showLocationPanel} onClose={() => setShowLocationPanel(false)} onCreated={onLocationCreated} />
    </motion.div>
  );
}
